import { Form } from "@components";
import Hooks from "@hooks";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@material-ui/core";
import React from "react";
import { Actions, Robot } from "../../../../../components";
import { User, useRequestContext } from "../../../../../context";
import { messages } from "./messages";

type Data = User & {
  has_started: "yes" | "no";
};

type Props = {
  error?: Error | null | undefined;
  onClose: () => void;
  onSubmit: (user: User, email?: string) => void;
  open: boolean;
  user?: User;
};

export const AddUserModal: React.FC<Props> = (props) => {
  const [requestContextState, requestContextDispatch] = useRequestContext();
  const form = Form.useManager<Data>({
    id: "",
    name: "",
    email: "",
    start_date: undefined,
    has_started: "yes",
  });
  const [userIdInput, setUserIdInput] = React.useState<string>("");
  // Single backend lookup (matches by EmployeeEmail OR Upn) returns id/name/email
  // together - works the same for Google and Entra sessions, no Directory API needed.
  const [employee, employee_error, employee_fetching] =
    Hooks.useEmployeeByEmail(
      Form.Validators.isEmail(form.values.email) ? "" : form.values.email
    );
  const prev = {
    form_errors: Hooks.usePrevious(form.errors),
    form_values: Hooks.usePrevious(form.values),
    props_error: Hooks.usePrevious(props.error),
    employee_fetching: Hooks.usePrevious(employee_fetching),
  };

  const [message, setMessage] = React.useState<{
    text: string;
    error?: boolean;
  }>({
    text: messages.has_started,
  });

  // Effects

  React.useEffect(() => {
    if (employee) {
      if (employee.name !== form.values.name) {
        form.set(Form.target.VALUE, ["name", employee.name]);
      }
      if (employee.id !== form.values.id && !userIdInput) {
        form.set(Form.target.VALUE, ["id", employee.id]);
        setUserIdInput(employee.id);
      } else if (userIdInput !== form.values.id) {
        form.set(Form.target.VALUE, ["id", form.values.id]);
        setUserIdInput(form.values.id);
      }
    } else if (!employee_fetching && form.values.name) {
      if (requestContextState.values.type !== "revoke") {
        form.set(Form.target.VALUE, ["name", ""]);
      }
    }
  }, [
    employee,
    employee_fetching,
    form,
    userIdInput,
    requestContextState.values.type,
  ]);

  React.useEffect(() => {
    if (props.user) {
      form.set(
        Form.target.VALUES,
        Object.assign(
          {
            id: props.user.id,
            name: props.user.name,
            email: props.user.email,
          },
          props.user.start_date
            ? {
                has_started: "no",
                start_date: props.user.start_date,
              }
            : {
                has_started: "yes",
              }
        ) as Data
      );
    }
  }, [form, props.user]); // [TODO] Handle 'form.set' dependency

  React.useEffect(() => {
    if (!prev.props_error && props.error) {
      setMessage({
        text: props.error.message,
        error: true,
      });
    } else if (
      prev.employee_fetching &&
      !employee_fetching &&
      !!employee_error
    ) {
      setMessage({ text: messages.user_id_not_found, error: true });
    }
  }, [
    prev.props_error,
    props.error,
    form,
    employee_fetching,
    employee_error,
    employee,
    prev.employee_fetching,
    prev.form_values,
  ]);

  // Handlers

  function handleMessage(error: any) {
    setMessage({
      text: !!error
        ? messages.form_has_errors
        : form.values.has_started === "yes"
        ? messages.has_started
        : messages.has_not_started,
      error: !!error,
    });
  }

  function handleHasStartedValueChange(value: "yes" | "no"): void {
    form.set(Form.target.VALUE, ["has_started", value]);
    setMessage({
      text: value === "yes" ? messages.has_started : messages.has_not_started,
    });
    if (value === "yes" && form.errors.start_date) {
      form.set(Form.target.ERROR, ["start_date", undefined]);
    }
  }

  function handleRevokeReasonValueChange(
    value: "termination" | "unnecessary"
  ): void {
    requestContextDispatch({
      type: "set-revoke-type",
      value,
    });
  }

  const handleEmailValueChange = React.useCallback(
    (name: string, value: string) => {
      form.set(Form.target.VALUES, {
        id: "",
        email: value,
      });
      form.set(Form.target.ERRORS, {
        id: undefined,
        name: undefined,
        email: undefined,
      });
      validate([name, value]).then(([name, error]) => handleMessage(error));
      if (prev.form_values?.email !== form.values.email && !!employee_error) {
        setMessage({ text: messages.user_id_not_found, error: true });
      }
    },
    [form, handleMessage, prev.form_values, employee_error, validate]
  );

  function handleEmailValueInput(name: string, value: string): void {
    form.set(Form.target.VALUE, ["email", value]);
  }

  function handleNameValueInput(name: string, value: string): void {
    form.set(Form.target.VALUE, ["name", value]);
  }

  function handleUserIDValueChange(name: string, value: string): void {
    form.set(Form.target.VALUE, ["id", value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
      handleMessage(error);
    });
  }

  function handleStartDateValueChange(name: string, value: any): void {
    form.set(Form.target.VALUE, ["start_date", value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
      handleMessage(error);
    });
  }

  function handleCancelButtonClick(): void {
    props.onClose();
    form.reset();
    setMessage({ text: messages.has_started, error: false });
  }

  function handleAddUserButtonClick(): void {
    // employee is already resolved reactively as the admin types (Hooks.useEmployeeByEmail) -
    // re-querying here would just race that same lookup instead of reusing its result.
    if (employee) {
      addUser();
      return;
    }

    if (!form.values.email) {
      Promise.all(Object.entries(form.values).map(validate)).then(
        (validations) => {
          const valid = validations.reduce((valid, [name, error]) => {
            return valid && !error;
          }, true);

          if (valid) {
            // do nothing
          } else {
            validations.forEach(([name, error]) => {
              form.set(Form.target.ERROR, [name, error]);
            });
          }
        }
      );
    } else {
      setMessage({ text: messages.user_id_not_found, error: true });
    }
    form.set(Form.target.VALUE, ["id", ""]);
    form.set(Form.target.VALUE, ["name", ""]);
  }

  function addUser() {
    if (message.text !== messages.user_id_not_found) {
      Promise.all(Object.entries(form.values).map(validate)).then(
        (validations) => {
          const valid = validations.reduce((valid, [name, error]) => {
            return valid && !error;
          }, true);

          if (valid) {
            props.onSubmit(
              Object.assign(
                {
                  id: form.values.id,
                  name: form.values.name,
                  email: form.values.email,
                },
                form.values.has_started === "no"
                  ? {
                      start_date: form.values.start_date,
                    }
                  : {}
              ),
              props.user?.email
            );
            setMessage({
              text:
                form.values.has_started === "yes"
                  ? messages.has_started
                  : messages.has_not_started,
              error: false,
            });
            form.reset();
          } else {
            validations.forEach(([name, error]) => {
              form.set(Form.target.ERROR, [name, error]);
            });
            setMessage({
              text: messages.form_has_errors,
              error: true,
            });
          }
        }
      );
    }
  }

  // Helpers

  function isAddUserButtonDisabled(): boolean {
    return employee_fetching;
  }

  function validate([name, value]: [string, any]): Promise<
    [keyof Data, Error?]
  > {
    let error: Error | undefined;

    switch (name) {
      case "id":
      case "name":
        error = Form.Validators.hasValue(value);
        break;
      case "email":
        error =
          Form.Validators.hasValue(value) || Form.Validators.isEmail(value);
        break;
      case "start_date":
        if (form.values.has_started === "no") {
          error = Form.Validators.hasValue(value);
        }
        break;
    }

    return Promise.resolve([name as keyof Data, error]);
  }

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      onClose={props.onClose}
      open={props.open}
      scroll="paper"
    >
      <DialogTitle disableTypography>
        <Grid container spacing={2}>
          <Grid item xs={3} />
          <Grid item xs={9}>
            <Typography variant="subtitle2">Add User</Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Robot message={message.text} error={message.error} />
          </Grid>
          <Grid item xs={9}>
            <Form>
              {requestContextState.values.type === "revoke" ? (
                <Form.Group label="Reason for revocation" radio>
                  <Form.Radiobutton
                    label="Resignation/Termination"
                    name="termination"
                    onChange={handleRevokeReasonValueChange}
                    size={3}
                    value={
                      requestContextState.values.revoke_type === "termination"
                    }
                  />
                  <Form.Radiobutton
                    label="Unnecessary Access"
                    name="unnecessary"
                    onChange={handleRevokeReasonValueChange}
                    size={3}
                    value={
                      requestContextState.values.revoke_type === "unnecessary"
                    }
                  />
                </Form.Group>
              ) : (
                <Form.Group
                  label="Has the employee already started in the company?"
                  radio
                >
                  <Form.Radiobutton
                    label="Yes"
                    name="yes"
                    onChange={handleHasStartedValueChange}
                    size={2}
                    value={form.values.has_started === "yes"}
                  />
                  <Form.Radiobutton
                    label="No"
                    name="no"
                    onChange={handleHasStartedValueChange}
                    size={2}
                    value={form.values.has_started === "no"}
                  />
                </Form.Group>
              )}
              <Form.Autocomplete
                error={form.errors.email}
                label="Email"
                loading={employee_fetching}
                name="email"
                onChange={handleEmailValueChange}
                onInput={handleEmailValueInput}
                options={[]}
                size={6}
                value={form.values.email}
              />
              {requestContextState.values.revoke_type === "termination" ? (
                <Form.Textbox
                  error={form.errors.name}
                  label="Name"
                  name="name"
                  onChange={handleNameValueInput}
                  size={6}
                  value={form.values.name}
                />
              ) : (
                <Form.Textbox
                  disabled
                  error={form.errors.name}
                  label="Name"
                  name="name"
                  size={6}
                  value={form.values.name}
                />
              )}

              <Form.Textbox
                error={form.errors.id}
                label="Employee ID"
                loading={employee_fetching}
                name="id"
                onChange={handleUserIDValueChange}
                size={6}
                value={form.values.id}
              />
              {form.values.has_started === "no" ? (
                <Form.Datebox
                  disablePast
                  error={form.errors.start_date}
                  label="Employee Start Date"
                  name="start_date"
                  onChange={handleStartDateValueChange}
                  size={6}
                  value={form.values.start_date}
                />
              ) : null}
            </Form>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Actions>
              <Actions.Action outlined onClick={handleCancelButtonClick}>
                Cancel
              </Actions.Action>
              <Actions.Action
                disabled={isAddUserButtonDisabled()}
                onClick={handleAddUserButtonClick}
              >
                Add User
              </Actions.Action>
            </Actions>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
